import { React, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { toast } from 'react-toastify';
import { ReactComponent as CancelSvg } from 'assets/images/icons/close_24px.svg';
import { ReactComponent as SaveIcon } from 'assets/images/icons/save-1.svg';
import { ClipLoader } from 'react-spinners';
import { InputComponent } from 'component/Input';
import { TextareaComponent } from 'component/Textarea';
import { SelectComponent } from 'component/Select';
import { ButtonComponent } from 'component/Button';
import { axiosWrapper } from 'utils/Helper';
import { getDonorById, updateDonor } from 'api/DonorsApi';
import { getOrganizations } from 'api/CustomersApi';

function buildOrganizationOptions(list) {
  const organizations = Array.isArray(list) ? list : [];
  return organizations.map((organization) => {
    const label =
      organization.full_name_uk?.trim() ||
      organization.name ||
      organization.abbreviation ||
      String(organization.id);
    return { value: organization.id, label };
  });
}

function buildSelectedFromDonor(organizationsDetail, organizationsIds) {
  const fromDetail = organizationsDetail ?? [];
  if (Array.isArray(fromDetail) && fromDetail.length) {
    return fromDetail
      .filter((organization) => organization && typeof organization === 'object')
      .map((organization) => ({
        value: organization.id,
        label:
          organization.full_name_uk?.trim() ||
          organization.name ||
          organization.abbreviation ||
          String(organization.id),
      }));
  }
  const ids = organizationsIds ?? [];
  const arr = Array.isArray(ids) ? ids : [];
  return arr
    .map((raw) => {
      const id = typeof raw === 'number' || typeof raw === 'string' ? Number(raw) : raw?.id;
      if (!Number.isFinite(id)) return null;
      return {
        value: id,
        label:
          typeof raw === 'object' && raw
            ? raw.full_name_uk?.trim() || raw.name || raw.abbreviation || String(id)
            : String(id),
      };
    })
    .filter(Boolean);
}

export function DonorDialog({ mode, donorId, onSuccess, triggerIcon }) {
  const [open, setOpen] = useState(false);
  const [loader, setLoader] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);

  const isView = mode === 'view';
  const isEdit = mode === 'edit';

  const handleOpen = () => {
    setOpen(true);
    setLoader(!!donorId);

    const fetchOrgs = () =>
      new Promise((resolve, reject) => {
        axiosWrapper(() => {
          getOrganizations().then(resolve).catch(reject);
        });
      });

    Promise.all([fetchOrgs(), donorId ? getDonorById(donorId) : Promise.resolve(null)])
      .then(([orgRes, donorRes]) => {
        const list = orgRes?.data?.results ?? orgRes?.data;
        const options = buildOrganizationOptions(list);
        setOrganizationOptions(options);

        if (donorRes?.data) {
          const donor = donorRes.data;
          setName(donor.name || '');
          setDescription(donor.description != null ? String(donor.description) : '');
          const rawSelected = buildSelectedFromDonor(
            donor.organizations_detail,
            donor.organizations
          );
          const selected = rawSelected.map((s) => {
            const match = options.find((o) => o.value === s.value);
            return match ? { value: s.value, label: match.label } : s;
          });
          setSelectedOrganizations(selected);
        } else {
          setName('');
          setDescription('');
          setSelectedOrganizations([]);
        }
      })
      .catch(() => {
        toast.error(donorId ? 'Failed to load donor' : 'Failed to load organizations');
        setOrganizationOptions([]);
        if (donorId) {
          setName('');
          setDescription('');
          setSelectedOrganizations([]);
        }
      })
      .finally(() => setLoader(false));
  };

  const handleClose = () => setOpen(false);

  const handleSave = () => {
    if (!name?.trim()) {
      toast.warning('Donor name is required');
      return;
    }
    if (!selectedOrganizations?.length) {
      toast.warning('Select at least one organization');
      return;
    }
    setLoader(true);
    const body = {
      name: name.trim(),
      description: description.trim() || undefined,
      organizations: selectedOrganizations.map((option) => option.value),
    };
    updateDonor(donorId, body)
      .then(() => {
        toast.success('Donor updated');
        handleClose();
        onSuccess?.();
      })
      .catch((err) => {
        const data = err?.response?.data;
        let message = typeof data?.detail === 'string' ? data.detail : null;
        if (!message && data && typeof data === 'object') {
          const parts = Object.entries(data).map(([k, v]) => {
            if (Array.isArray(v)) return `${k}: ${v.join(', ')}`;
            if (v && typeof v === 'object') return `${k}: ${JSON.stringify(v)}`;
            return `${k}: ${v}`;
          });
          if (parts.length) message = parts.join('; ');
        }
        toast.error(message || 'Donor not updated');
      })
      .finally(() => setLoader(false));
  };

  const dialogTitle = isEdit ? 'Edit donor' : 'Donor details';

  return (
    <>
      <button type="button" className="icon-action-btn" onClick={handleOpen} title={dialogTitle}>
        {triggerIcon}
      </button>
      <Dialog open={open} onClose={handleClose} aria-labelledby="donor-dialog-title">
        <div className="confirm-popup donor-form-popup">
          <div className="header">
            <span className="title" id="donor-dialog-title">
              {dialogTitle}
            </span>
            <CancelSvg className="closeBtn" onClick={handleClose} />
          </div>
          {loader && donorId ? (
            <div className="flex-column-group" style={{ padding: '24px', alignItems: 'center' }}>
              <ClipLoader color="rgba(0, 177, 183, 1)" size={32} />
            </div>
          ) : (
            <div className="flex-column-group" style={{ padding: '0 24px 24px' }}>
              <InputComponent
                id="donor-dialog-name"
                name="name"
                label="Donor name"
                placeholder="Donor name"
                value={name}
                handleInput={isView ? undefined : setName}
                classNameWrapper="align-left w-100"
                disabled={isView}
              />
              <div className="input-wrapper align-left w-100" style={{ marginTop: '10px' }}>
                <div className="labels">
                  <label htmlFor="textarea-donor-dialog-description">Description</label>
                </div>
                <div className="textarea-wrapper w-100">
                  <TextareaComponent
                    id="textarea-donor-dialog-description"
                    name="description"
                    placeholder="Description"
                    value={description}
                    handleTextarea={isView ? () => {} : setDescription}
                    disabled={isView}
                    minRows={1}
                    maxRows={20}
                    style={{ width: '100%', boxSizing: 'border-box', minHeight: 'unset' }}
                  />
                </div>
              </div>
              <div className="select-wrapper w-100" style={{ marginTop: '10px' }}>
                <span className="label">Organizations</span>
                <SelectComponent
                  className="w-100"
                  placeholder="Select organizations..."
                  value={selectedOrganizations}
                  options={organizationOptions}
                  handleSelect={(selected) =>
                    setSelectedOrganizations(Array.isArray(selected) ? selected : [])
                  }
                  isMulti
                  isDisabled={isView}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  noOptionsMessage={() => 'No organizations'}
                  customStyles={{
                    menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                    menu: (base) => ({ ...base, zIndex: 2000 }),
                  }}
                />
              </div>
              {isEdit && (
                <div className="buttons-group" style={{ marginTop: '16px' }}>
                  <ButtonComponent
                    iconLeft={<CancelSvg />}
                    className="danger"
                    handleButton={handleClose}
                    label="Cancel"
                  />
                  <ButtonComponent
                    iconLeft={
                      loader ? <ClipLoader color="rgba(0, 177, 183, 1)" size={20} /> : <SaveIcon />
                    }
                    handleButton={handleSave}
                    label="Save"
                    disabled={loader}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </Dialog>
    </>
  );
}
